import { useEffect, useRef, useState } from 'react'
import { useOnboardingStore } from './store/onboardingStore'
import { trackStepView } from './lib/analytics'
import { isMetaVariant, isAdvertorialVariant } from './lib/variant'
import { META_COPY } from './lib/metaCopy'
import { Advertorial } from './components/Advertorial'
import {
  HookStep,
  PainPointsStep,
  SolutionStep,
  SocialProofStep,
  PrimaryGoalStep,
  KidneyStatusStep,
  DiagnosisTimelineStep,
  ComorbiditiesStep,
  WeightStep,
  DialysisStep,
  DietKnowledgeStep,
  StrugglesStep,
  EatingStyleStep,
  TestimonialStep,
  MotivationStep,
  CommitmentStep,
  FeaturePriorityStep,
  EmailStep,
  AnalysisStep,
  ProfileRevealStep,
  DemoScanStep,
  UserScanStep,
  OfferStep,
  CreateAccountStep,
  SuccessStep,
} from './steps'

// Steps with dark/custom backgrounds where footer should be hidden
// Steps where footer is hidden (full-screen experiences)
const DARK_BACKGROUND_STEPS = [
  'hook',
  'solution',
  'testimonial',
  'analysis',
  'demoScan',
  'userScan',
  'success',
]

const ADVERTORIAL_CONTINUED_KEY = 'kidneypal_advertorial_continued'

function App() {
  const { getSteps, currentStep, setCurrentStep, goBack } = useOnboardingStore()
  const steps = getSteps()
  const currentStepKey = steps[currentStep]?.key
  const lastStepRef = useRef(currentStep)
  const isDarkBackground = DARK_BACKGROUND_STEPS.includes(currentStepKey || '')
  const isMeta = isMetaVariant()

  // Meta-advertorial variant shows a Linda-story pre-page before the funnel.
  // Once the reader clicks the CTA, we flip a sessionStorage flag so a refresh
  // mid-funnel doesn't bounce them back to the article.
  const [advertorialDone, setAdvertorialDone] = useState(() => {
    if (typeof window === 'undefined') return false
    return sessionStorage.getItem(ADVERTORIAL_CONTINUED_KEY) === '1'
  })
  const showAdvertorial = isAdvertorialVariant() && !advertorialDone

  const handleAdvertorialContinue = () => {
    try {
      sessionStorage.setItem(ADVERTORIAL_CONTINUED_KEY, '1')
    } catch {}
    setAdvertorialDone(true)
  }

  // Dev skip: ?step=offer (or any step key) to jump directly
  // useEffect(() => {
  //   const params = new URLSearchParams(window.location.search)
  //   const skipTo = params.get('step')
  //   if (skipTo) {
  //     const idx = steps.findIndex(s => s.key === skipTo)
  //     if (idx >= 0) setCurrentStep(idx)
  //   }
  // }, [])

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentStep])

  // Track step views in GA4
  useEffect(() => {
    if (currentStepKey) {
      trackStepView(currentStepKey, currentStep)
    }
  }, [currentStepKey, currentStep])

  // Push to history when moving forward
  useEffect(() => {
    if (currentStep > lastStepRef.current) {
      window.history.pushState({ step: currentStep }, '')
    }
    lastStepRef.current = currentStep
  }, [currentStep])

  // Handle browser back button
  useEffect(() => {
    const handlePopState = () => {
      if (currentStep > 0) {
        goBack()
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [currentStep, goBack])

  const renderStep = () => {
    switch (currentStepKey) {
      // Meta-short entry
      case 'hook': return <HookStep />
      // Profile
      case 'painPoints': return <PainPointsStep />
      case 'solution': return <SolutionStep />
      case 'socialProof': return <SocialProofStep />
      case 'primaryGoal': return <PrimaryGoalStep />
      case 'kidneyStatus': return <KidneyStatusStep />
      case 'diagnosisTimeline': return <DiagnosisTimelineStep />
      case 'comorbidities': return <ComorbiditiesStep />
      case 'weight': return <WeightStep />
      case 'dialysis': return <DialysisStep />
      // Lifestyle
      case 'dietKnowledge': return <DietKnowledgeStep />
      case 'struggles': return <StrugglesStep />
      case 'eatingStyle': return <EatingStyleStep />
      case 'testimonial': return <TestimonialStep />
      case 'motivation': return <MotivationStep />
      case 'commitment': return <CommitmentStep />
      case 'featurePriority': return <FeaturePriorityStep />
      // Motivation
      case 'email': return <EmailStep />
      case 'analysis': return <AnalysisStep />
      case 'profileReveal': return <ProfileRevealStep />
      case 'demoScan': return <DemoScanStep />
      case 'userScan': return <UserScanStep />
      // Plan
      case 'offer': return <OfferStep />
      case 'createAccount': return <CreateAccountStep />
      case 'success': return <SuccessStep />
      default: return <PainPointsStep />
    }
  }

  if (showAdvertorial) {
    return <Advertorial onContinue={handleAdvertorialContinue} />
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="flex-1">
        {renderStep()}
      </div>
      {!isDarkBackground && (
        <footer className="py-4 px-6 text-center text-xs text-gray-400 border-t border-gray-100">
          {isMeta && (
            <p className="mb-2 max-w-md mx-auto">{META_COPY.legalDisclaimer}</p>
          )}
          <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">
            Terms of Service
          </a>
          <span className="mx-2">·</span>
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">
            Privacy Policy
          </a>
          <span className="mx-2">·</span>
          <a href="mailto:support@kidneypal.app" className="hover:text-gray-600">
            Contact
          </a>
        </footer>
      )}
    </div>
  )
}

export default App
